import React, { 
  useEffect,
 } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useAutomaticProgressDialogActions } from "utils/context/AutomaticProgressDialogContext";
import { useFiles } from "utils/context/FilesContext";
import { usePricingActions } from "utils/context/PricingContext";
import { PricingErrorMessageProvider } from "./PricingErrorMessageProvider";
import { SideNavProvider } from "./SideNavProvider";
import NewFileForm from "./components/file-form-layout/CreateFile";
import { PERMISSIONS, SessionStorageKeys } from "utils/data/enum";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";

interface Props {
  saveFileRef: any;
  saveFileChangeRef: any;
  isFromExternalUrlLink?: boolean;
}

const CreateFilePage = ({ saveFileRef, saveFileChangeRef, isFromExternalUrlLink = false }: Props) => {
  const [, { getFileById, resetInitialValues }] = useFiles();
  const [{ profileSettings }] = useProfileSettingsCache();
  const [, { setPricingNotificationUpdated }] = usePricingActions();
  const [, { openAutomaticProgressDialog, closeAutomaticProgressDialog }] =
    useAutomaticProgressDialogActions();
  const navigate = useNavigate();
  const {encodedFileId} = useParams();
  const location = useLocation();
  const fileID = location?.state?.fileID as string ?? undefined;
  const integrationKey = location?.state?.integrationKey as string ?? undefined;
  // console.log("fileID:", fileID, "integrationKey:", integrationKey);
  const isExistingFile:boolean = location?.state?.isExistingFile ?? false;
  // console.log("isExistingFile:", isExistingFile);
  const isCreateFileAccess = !(profileSettings?.userPermissionItemTypes as string[] | undefined)?.includes(
    PERMISSIONS.FILES_VIEW,
  );
  if (isCreateFileAccess) {
    window.location.href = "/403";
  } 

  useEffect(() => {
    const isAdvSearchOpen = (location?.state?.isAdvSearchOpen as boolean) ?? undefined;
    const handlePopstate = () => {
      if (isAdvSearchOpen) {
        sessionStorage.setItem(SessionStorageKeys.USE_PRIOR_SEARCH_CRITERIA_ADVANCE_SEARCH, "1");
        sessionStorage.removeItem(SessionStorageKeys.USE_PRIOR_SEARCH_CRITERIA);
      } else {
        sessionStorage.removeItem(SessionStorageKeys.USE_PRIOR_SEARCH_CRITERIA_ADVANCE_SEARCH);
      }
      sessionStorage.setItem(SessionStorageKeys.USE_PRIOR_SEARCH_CRITERIA, "1");
    };
    window.addEventListener("popstate", handlePopstate);
  }, [fileID]);

 useEffect(() => {
    if (isFromExternalUrlLink || !fileID)  {
      resetInitialValues();
      return;
    }

    const getFileAsync = async () => {
      openAutomaticProgressDialog();
      setPricingNotificationUpdated(false);
      setTimeout(async () => {
        const response = await getFileById(btoa(`${fileID}|${integrationKey}`));
        if (!response || response instanceof Error) {
          navigate("/500", { replace: true });
        }
      }, 500);
      setPricingNotificationUpdated(true);
      //DELAY UNTIL REACT-HOOK-FORM IS FULLY LOADED.
      closeAutomaticProgressDialog(300);
    };

    getFileAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileID, getFileById]);

 useEffect(() => {
    if (!isFromExternalUrlLink || !encodedFileId )  {
      resetInitialValues();
      return;
    }

    const getFileAsync = async () => {
      openAutomaticProgressDialog();
      setPricingNotificationUpdated(false);
      setTimeout(async () => {
        const response = await getFileById(encodedFileId);
        if (!response || response instanceof Error) {
          navigate("/500", { replace: true });
        }
      }, 200);
      setPricingNotificationUpdated(true);
      //DELAY UNTIL REACT-HOOK-FORM IS FULLY LOADED.
      closeAutomaticProgressDialog(300);
    };

    getFileAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encodedFileId, getFileById]);  
  
  return (
    <PricingErrorMessageProvider>
      <SideNavProvider>
        <NewFileForm
          saveFileRef={saveFileRef}
          saveFileChangeRef={saveFileChangeRef}
          isExistingFile={isExistingFile}
        />
      </SideNavProvider>
    </PricingErrorMessageProvider>
  );
};

export default CreateFilePage;
